/**
 * A colour's readable shade — `FRONTEND.md` §9.27, owner decision 2026-08-05.
 *
 * The category chip draws its label **in the category's own colour**, which is what makes one
 * findable among twenty at a glance. That is also why free hex was refused for months: a pale yellow
 * chosen from a colour wheel is pale yellow text on a near-white card.
 *
 * The owner's answer was neither *keep twelve colours* nor *stop colouring the label*: adjust the
 * shade. Hue and saturation are held — a yellow stays a yellow — and only lightness moves, in
 * whichever direction the background needs, until the contrast ratio clears the readable threshold.
 */

/** WCAG AA for normal text. Chips are small and often read in poor light, so the floor is not lowered. */
const MIN_CONTRAST = 4.5

/** One step of lightness per attempt. 1 % keeps the result close to what was picked. */
const STEP = 0.01

export interface Rgb {
  r: number
  g: number
  b: number
}

/** `#3b82f6` → its channels, or `null` when it is not a six-digit hex. */
export function parseHex(value: unknown): Rgb | null {
  if (typeof value !== 'string') return null
  const match = /^#([0-9a-f]{6})$/i.exec(value.trim())
  if (match === null) return null
  const digits = match[1] ?? ''
  return {
    r: Number.parseInt(digits.slice(0, 2), 16),
    g: Number.parseInt(digits.slice(2, 4), 16),
    b: Number.parseInt(digits.slice(4, 6), 16),
  }
}

function toHex({ r, g, b }: Rgb): string {
  const pair = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0')
  return `#${pair(r)}${pair(g)}${pair(b)}`
}

/** Relative luminance, the WCAG definition — the sRGB channels linearised, then weighted. */
export function luminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const scaled = value / 255
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** How far apart two colours read, 1 (identical) to 21 (black on white). */
export function contrast(one: Rgb, other: Rgb): number {
  const first = luminance(one)
  const second = luminance(other)
  const lighter = Math.max(first, second)
  const darker = Math.min(first, second)
  return (lighter + 0.05) / (darker + 0.05)
}

function toHsl({ r, g, b }: Rgb): { hue: number; saturation: number; lightness: number } {
  const red = r / 255
  const green = g / 255
  const blue = b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2
  if (max === min) return { hue: 0, saturation: 0, lightness }
  const delta = max - min
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
  const hue =
    max === red
      ? ((green - blue) / delta + (green < blue ? 6 : 0)) / 6
      : max === green
        ? ((blue - red) / delta + 2) / 6
        : ((red - green) / delta + 4) / 6
  return { hue, saturation, lightness }
}

function toRgb({ hue, saturation, lightness }: { hue: number; saturation: number; lightness: number }): Rgb {
  if (saturation === 0) {
    const grey = lightness * 255
    return { r: grey, g: grey, b: grey }
  }
  const upper = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation
  const lower = 2 * lightness - upper
  const channel = (offset: number) => {
    let point = hue + offset
    if (point < 0) point += 1
    if (point > 1) point -= 1
    if (point < 1 / 6) return lower + (upper - lower) * 6 * point
    if (point < 1 / 2) return upper
    if (point < 2 / 3) return lower + (upper - lower) * (2 / 3 - point) * 6
    return lower
  }
  return { r: channel(1 / 3) * 255, g: channel(0) * 255, b: channel(-1 / 3) * 255 }
}

/**
 * The chosen colour, darkened or lightened until its label reads against the card.
 *
 * `onDark` says which way to walk: a dark card needs a lighter ink, a light card a darker one.
 * Returned unchanged when it already clears the threshold, so a colour picked off the palette is the
 * colour that is drawn.
 *
 * Falls back to the card's opposite when the hue cannot get there — a mid-grey on a mid-grey card has
 * no shade of itself that reads, and *unreadable* is worse than *not quite the colour asked for*.
 */
export function readableInk(colour: string, onDark = false): string {
  const rgb = parseHex(colour)
  if (rgb === null) return colour
  // What the label actually sits on: the card, near-white in the light theme and near-black in dark.
  const background: Rgb = onDark ? { r: 24, g: 24, b: 27 } : { r: 255, g: 255, b: 255 }
  if (contrast(rgb, background) >= MIN_CONTRAST) return toHex(rgb)

  const { hue, saturation } = toHsl(rgb)
  for (let step = 1; step <= 100; step += 1) {
    const moved = onDark ? toHsl(rgb).lightness + step * STEP : toHsl(rgb).lightness - step * STEP
    if (moved < 0 || moved > 1) break
    const candidate = toRgb({ hue, saturation, lightness: moved })
    if (contrast(candidate, background) >= MIN_CONTRAST) return toHex(candidate)
  }
  return onDark ? '#ffffff' : '#000000'
}
